import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BsGrid3X3GapFill,
  BsPersonCircle,
  BsBoxArrowRight,
  BsChevronDown,
  BsMegaphoneFill,
  BsFileTextFill,
  BsPercent,
  BsList,
  BsX,
} from "react-icons/bs";
import logo from "../media/ngo-waters-logo.png";

const NAV_LINKS = [
  { label: "About Us", path: "/aboutus" },
  { label: "Quality Audit", path: "/quality" },
  { label: "HORECA", path: "/horeca" },
  { label: "Bulk Request", path: "/bulkorder" },
  { label: "FAQs", path: "/faq" },
  { label: "Feedback", path: "/feedback" },
];

const EXTRA_LINKS = [
  { label: "News", path: "/news", icon: <BsMegaphoneFill size={13} /> },
  { label: "Blog", path: "/blog", icon: <BsFileTextFill size={13} /> },
];

const Header = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    !!localStorage.getItem("authToken")
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const updateLoginStatus = useCallback(() => {
    setIsLoggedIn(!!localStorage.getItem("authToken"));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    updateLoginStatus();
    window.addEventListener("storage", updateLoginStatus);
    return () => window.removeEventListener("storage", updateLoginStatus);
  }, [updateLoginStatus]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    const confirmed = window.confirm("Are you sure you want to log out?");
    if (!confirmed) return;

    localStorage.removeItem("authToken");
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    updateLoginStatus();
    navigate("/login");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <header className="glass-header-wrap">
        <motion.div
          initial={{ y: -18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className={`glass-header ${scrolled ? "is-scrolled" : ""}`}
        >
          <div className="glass-header__inner">
            <Link to="/" className="brand-mark text-decoration-none">
              <motion.img
                src={logo}
                alt="NGO Waters"
                className="brand-logo"
                whileHover={{ rotate: [0, -8, 8, 0] }}
                transition={{ duration: 0.45 }}
              />
              <div className="brand-copy">
                {/* <span className="brand-title">
                  NGO <span>Waters</span>
                </span> */}
              </div>
            </Link>

            <nav className="desktop-nav d-none d-lg-flex" aria-label="Primary">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`nav-pill ${isActive(link.path) ? "active" : ""}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="header-actions d-none d-lg-flex" ref={dropdownRef}>
              <Link
                to="/discount"
                className="discount-cta text-decoration-none"
              >
                <BsPercent size={14} />
                <span>Get Discount</span>
              </Link>

              <button
                type="button"
                className={`menu-trigger ${isDropdownOpen ? "open" : ""}`}
                onClick={() => setIsDropdownOpen((prev) => !prev)}
                aria-expanded={isDropdownOpen}
                aria-label="Open menu"
              >
                <BsGrid3X3GapFill size={13} />
                <span>Menu</span>
                <BsChevronDown className="chevron" size={11} />
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 12, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 12, scale: 0.96 }}
                    transition={{ duration: 0.18 }}
                    className="glass-dropdown"
                  >
                    <div className="dropdown-section">
                      {isLoggedIn ? (
                        <>
                          <Link to="/profile" className="dropdown-link">
                            <BsPersonCircle size={15} />
                            <span>Profile</span>
                          </Link>
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="dropdown-link danger"
                          >
                            <BsBoxArrowRight size={15} />
                            <span>Logout</span>
                          </button>
                        </>
                      ) : (
                        <Link
                          to="/login"
                          className="dropdown-link primary-lite"
                        >
                          <BsPersonCircle size={15} />
                          <span>Login</span>
                        </Link>
                      )}
                    </div>

                    <div className="dropdown-divider" />

                    <div className="dropdown-section">
                      {EXTRA_LINKS.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="dropdown-link"
                        >
                          {item.icon}
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              type="button"
              className="mobile-toggle d-lg-none"
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <BsX size={24} /> : <BsList size={24} />}
            </button>
          </div>
        </motion.div>
      </header>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              className="mobile-backdrop d-lg-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-glass-panel d-lg-none"
              initial={{ opacity: 0, y: -18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.98 }}
              transition={{ duration: 0.22 }}
            >
              <nav className="mobile-nav-links" aria-label="Mobile primary">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`mobile-nav-link ${
                      isActive(link.path) ? "active" : ""
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/discount" className="mobile-nav-link highlight">
                  Exclusive Discount
                </Link>
              </nav>

              <div className="mobile-divider" />

              <div className="mobile-sub-links">
                {EXTRA_LINKS.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="mobile-sub-link"
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                ))}
              </div>

              <div className="mobile-divider" />

              <div className="mobile-auth-block">
                {isLoggedIn ? (
                  <>
                    <Link to="/profile" className="mobile-btn secondary">
                      <BsPersonCircle />
                      <span>Profile</span>
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="mobile-btn danger"
                    >
                      <BsBoxArrowRight />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <Link to="/login" className="mobile-btn primary">
                    <BsPersonCircle />
                    <span>Login</span>
                  </Link>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        :root {
          --nav-bg: rgba(255, 255, 255, 0.55);
          --nav-bg-strong: rgba(255, 255, 255, 0.72);
          --nav-border: rgba(255, 255, 255, 0.42);
          --nav-stroke: rgba(15, 23, 42, 0.08);
          --nav-shadow: 0 18px 50px rgba(15, 23, 42, 0.10);
          --nav-shadow-strong: 0 20px 60px rgba(15, 23, 42, 0.14);
          --text-main: #0f172a;
          --text-soft: #64748b;
          --brand: #1a3c6b;
          --accent: #36bfa8;
          --danger: #dc3545;
        }

        .glass-header-wrap {
          position: sticky;
          top: 0;
          z-index: 9999;
          width: 100%;
        }

        .glass-header {

          margin: 0 auto;
          border-radius: 999px;
          background: var(--nav-bg);
          backdrop-filter: blur(22px) saturate(180%);
          -webkit-backdrop-filter: blur(22px) saturate(180%);
          border: 1px solid var(--nav-border);
          box-shadow: var(--nav-shadow);
          transition: all 0.28s ease;
        }

        .glass-header.is-scrolled {
          background: var(--nav-bg-strong);
          box-shadow: var(--nav-shadow-strong);
        }

        .glass-header__inner {
          min-height: 74px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 18px;
        }

        .brand-mark {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          min-width: max-content;
        }

        .brand-logo {
          height: 42px;
          width: auto;
          object-fit: contain;
        }

        .brand-title {
          color: var(--text-main);
          font-weight: 800;
          font-size: 1.05rem;
          letter-spacing: -0.03em;
          line-height: 1;
          text-transform: uppercase;
        }

        .brand-title span {
          color: var(--accent);
        }

        .desktop-nav {
          align-items: center;
          gap: 8px;
          background: rgba(255,255,255,0.36);
          border: 1px solid rgba(255,255,255,0.3);
          border-radius: 999px;
          padding: 6px;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .nav-pill {
          position: relative;
          text-decoration: none;
          color: var(--text-soft);
          font-size: 0.92rem;
          font-weight: 600;
          padding: 10px 14px;
          border-radius: 999px;
          transition: all 0.24s ease;
          white-space: nowrap;
        }

        .nav-pill:hover {
          color: var(--brand);
          background: rgba(255,255,255,0.58);
        }

        .nav-pill.active {
          color: var(--brand);
          background: rgba(26, 60, 107, 0.09);
          box-shadow: inset 0 0 0 1px rgba(26, 60, 107, 0.08);
        }

        .header-actions {
          position: relative;
          align-items: center;
          gap: 10px;
        }

        .discount-cta,
        .menu-trigger,
        .mobile-toggle,
        .mobile-btn {
          min-height: 44px;
        }

        .discount-cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #1f8b74;
          font-size: 0.9rem;
          font-weight: 700;
          padding: 0 16px;
          border-radius: 999px;
          background: linear-gradient(
            135deg,
            rgba(54, 191, 168, 0.16),
            rgba(54, 191, 168, 0.08)
          );
          border: 1px solid rgba(54, 191, 168, 0.22);
        }

        .menu-trigger {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #334155;
          font-size: 0.9rem;
          font-weight: 700;
          padding: 0 16px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.62);
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
        }

        .menu-trigger .chevron {
          transition: transform 0.24s ease;
        }

        .menu-trigger.open .chevron {
          transform: rotate(180deg);
        }

        .glass-dropdown {
          position: absolute;
          top: calc(100% + 14px);
          right: 0;
          width: 240px;
          padding: 10px;
          border-radius: var(--radius-lg);
          background: rgba(255, 255, 255, 0.72);
          backdrop-filter: blur(22px) saturate(180%);
          -webkit-backdrop-filter: blur(22px) saturate(180%);
          border: 1px solid rgba(255,255,255,0.45);
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
        }

        .dropdown-section {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .dropdown-divider,
        .mobile-divider {
          height: 1px;
          background: rgba(15, 23, 42, 0.08);
          margin: 10px 2px;
        }

        .dropdown-link {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 11px 12px;
          border: 0;
          border-radius: 14px;
          background: transparent;
          color: #334155;
          font-size: 0.92rem;
          font-weight: 600;
          text-decoration: none;
          text-align: left;
          transition: all 0.22s ease;
        }

        .dropdown-link:hover {
          background: rgba(255,255,255,0.66);
          color: #0f172a;
        }

        .dropdown-link.primary-lite {
          background: rgba(26, 60, 107, 0.06);
          color: var(--brand);
        }

        .dropdown-link.danger {
          color: var(--danger);
        }

        .mobile-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          border-radius: 14px;
          color: var(--text-main);
          background: rgba(255,255,255,0.58);
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
        }

        .mobile-backdrop {
          position: fixed;
          inset: 0;
          z-index: 9997;
          background: rgba(15, 23, 42, 0.22);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        .mobile-glass-panel {
          position: fixed;
          top: 86px;
          left: 12px;
          right: 12px;
          z-index: 9998;
          padding: 16px;
          border-radius: 26px;
          background: rgba(255, 255, 255, 0.74);
          backdrop-filter: blur(24px) saturate(180%);
          -webkit-backdrop-filter: blur(24px) saturate(180%);
          border: 1px solid rgba(255,255,255,0.42);
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
        }

        .mobile-nav-links,
        .mobile-sub-links,
        .mobile-auth-block {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mobile-nav-link,
        .mobile-sub-link {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
          color: #334155;
          padding: 12px 14px;
          border-radius: 16px;
          font-weight: 600;
          background: transparent;
        }

        .mobile-nav-link.active {
          color: var(--brand);
          background: rgba(26, 60, 107, 0.08);
        }

        .mobile-nav-link.highlight {
          color: #1f8b74;
          background: rgba(54, 191, 168, 0.12);
        }

        .mobile-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          text-decoration: none;
          border-radius: 16px;
          font-weight: 700;
          padding: 12px 14px;
        }

        .mobile-btn.primary {
          background: var(--brand);
          color: #fff;
        }

        .mobile-btn.secondary {
          background: rgba(255,255,255,0.7);
          color: #0f172a;
          border: 1px solid rgba(15, 23, 42, 0.08);
        }

        .mobile-btn.danger {
          background: #dc3545;
          color: #fff;
          border: none;
        }

        @media (max-width: 1199.98px) {
          .glass-header {
            border-radius: 24px;
          }
        }

        @media (max-width: 575.98px) {
          .glass-header-wrap {
            padding-top: 10px;
          }

          .glass-header {
            width: calc(100% - 12px);
            border-radius: 22px;
          }

          .glass-header__inner {
            min-height: 68px;
            padding: 10px 12px;
          }

          .brand-logo {
            height: 36px;
          }

          .brand-title {
            font-size: 0.96rem;
          }

          .mobile-glass-panel {
            top: 78px;
            left: 8px;
            right: 8px;
            border-radius: 22px;
          }
        }
      `}</style>
    </>
  );
};

export default Header;
